import { Badge, Button, Flex, Switch, Text, TextArea, TextField } from "@radix-ui/themes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import { Dialog } from "@/components/shared/dialog";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import { TrashIcon, ArrowLeftIcon } from "@radix-ui/react-icons";
import { DashboardSquareIcon } from "@/v2/assets/icons/DashboardSquareIcon";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout/page-header/PageHeader";
import {
  getBrowserSkillConfig,
  isBrowserSkill,
  Skill,
  buildSkillSlug,
} from "@/v2/entities/skill/Skill.model";
import {
  useSkillApi,
  BrowserProfileIdResponse,
  UpdateBrowserSkillPayload,
} from "@/v2/entities/skill/Skill.api";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";
import styles from "./browser-skill-detail.module.scss";
import { buildLiveViewUrl } from "./browserSessionUtils";
import {
  BrowserSkillAuthContent,
  BrowserSkillAuthSession,
} from "./BrowserSkillAuthContent";
import { useBrowserSkillForm } from "./useBrowserSkillForm";
import { useGetSkills } from "@/v2/entities/skill/useGetSkills";

type AuthStep = "idle" | "starting" | "waiting" | "verifying";

type ActiveAuthSession = BrowserSkillAuthSession & { skillId: string };

export function BrowserSkillDetail() {
  const { skillId } = useParams();
  const navigate = useNavigate();
  const { workspace } = useWorkspace();
  const queryClient = useQueryClient();
  const {
    getSkill,
    createBrowserSession,
    stopBrowserSession,
    getBrowserProfileId,
    deleteBrowserProfile,
    deleteSkill,
    updateBrowserSkill,
  } = useSkillApi();
  const { data: allSkills } = useGetSkills();

  const [authStep, setAuthStep] = useState<AuthStep>("idle");
  const [authError, setAuthError] = useState<string>();
  const [profileId, setProfileId] = useState<string | null>(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authDialogBusy, setAuthDialogBusy] = useState(false);
  const [authSession, setAuthSession] = useState<ActiveAuthSession | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string>();
  const pollerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: skill, isLoading } = useQuery(
    ["skill", skillId],
    () => getSkill(skillId ?? ""),
    { enabled: Boolean(skillId) }
  );

  const browserConfig = useMemo(() => {
    if (!skill) return undefined;
    return getBrowserSkillConfig(skill);
  }, [skill]);

  const authenticationRequired = browserConfig?.requiresAuthentication;

  // Form hook for edit mode
  const formState = useBrowserSkillForm({
    mode: 'edit',
    initialValues: skill && browserConfig ? {
      name: skill.name,
      description: skill.description,
      startUrl: browserConfig.startUrl,
      profileName: browserConfig.profileName,
      slug: skill.slug,
    } : undefined,
    existingSkills: allSkills ?? [],
    skillId: skill?.id,
  });

  // Update mutation
  const { mutateAsync: updateSkillMutation, isLoading: isUpdating } = useMutation<
    Skill,
    unknown,
    UpdateBrowserSkillPayload
  >({
    mutationFn: updateBrowserSkill,
    onSuccess: () => {
      queryClient.invalidateQueries(['skill', skillId]);
      queryClient.invalidateQueries(['skills']);
      toast.success('skill updated successfully');
    },
    onError: () => {
      toast.error('failed to update skill');
    },
  });

  // Reset form when skill data loads
  useEffect(() => {
    if (skill && browserConfig) {
      formState.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skill, browserConfig]);

  const profileName = useMemo(() => {
    if (!skill) return undefined;
    const existing = browserConfig?.profileName;
    const tenant = workspace || "unknown";
    return existing && existing.trim().length > 0
      ? existing
      : `${tenant}/${skill.slug}`;
  }, [browserConfig?.profileName, skill, workspace]);

  const fetchProfileId = useCallback(async () => {
    if (!profileName) {
      return null;
    }
    try {
      const result: BrowserProfileIdResponse = await getBrowserProfileId(
        profileName
      );
      const nextId = result.profileId ?? null;
      setProfileId(nextId);
      return nextId;
    } catch {
      setAuthError("Failed to check profile status.");
      return null;
    }
  }, [getBrowserProfileId, profileName]);

  const clearPoller = useCallback(() => {
    if (pollerRef.current) {
      clearInterval(pollerRef.current);
      pollerRef.current = null;
    }
  }, []);

  const startProfilePolling = useCallback(() => {
    clearPoller();
    setAuthStep("verifying");
    let attempts = 0;
    const maxAttempts = 30;
    pollerRef.current = setInterval(async () => {
      attempts += 1;
      const id = await fetchProfileId();
      if (id) {
        clearPoller();
        setAuthStep("idle");
        toast.success("Authentication connected.");
      } else if (attempts >= maxAttempts) {
        clearPoller();
        setAuthError("Authentication did not complete. Try again.");
        setAuthStep("idle");
      }
    }, 2000);
  }, [clearPoller, fetchProfileId]);

  useEffect(() => {
    return () => clearPoller();
  }, [clearPoller]);

  useEffect(() => {
    if (profileName) {
      fetchProfileId();
    }
  }, [fetchProfileId, profileName]);

  const handleStartAuthentication = useCallback(async () => {
    if (!skill || !browserConfig || !profileName) {
      setAuthError("Skill details are missing.");
      return;
    }
    setAuthError(undefined);
    setAuthDialogBusy(false);
    setAuthSession(null);
    setAuthStep("starting");
    try {
      const session = await createBrowserSession(
        browserConfig.startUrl,
        profileName
      );

      const profileLookup = await getBrowserProfileId(profileName);

      if (!profileLookup.profileId) {
        await stopBrowserSession(session.id, profileName);
        setAuthError("Failed to create authentication profile.");
        setAuthStep("idle");
        return;
      }

      const liveViewUrl = buildLiveViewUrl(session);
      if (!liveViewUrl) {
        await stopBrowserSession(session.id, profileName);
        setAuthError("Live view is not available for this session.");
        setAuthStep("idle");
        return;
      }
      setAuthSession({
        sessionId: session.id,
        liveViewUrl,
        profileName,
        profileId: profileLookup.profileId,
        skillId: skill.id,
      });
      setAuthDialogOpen(true);
      setAuthStep("waiting");
    } catch {
      setAuthError("Failed to start authentication.");
      setAuthStep("idle");
    }
  }, [browserConfig, createBrowserSession, getBrowserProfileId, profileName, skill, stopBrowserSession]);

  const handleFinishAuthentication = useCallback(async () => {
    if (!authSession) {
      return;
    }
    setAuthError(undefined);
    setAuthDialogBusy(true);
    try {
      await stopBrowserSession(authSession.sessionId, authSession.profileName);
      await updateBrowserSkill({
        id: authSession.skillId,
        name: skill?.name ?? "",
        description: skill?.description ?? "",
        slug: skill?.slug ?? "",
        startUrl: browserConfig?.startUrl ?? "",
        profileName: authSession.profileName,
      });
      queryClient.invalidateQueries(["skill", authSession.skillId]);
      queryClient.invalidateQueries(["skills"]);
    } catch {
      setAuthError("Failed to finish authentication.");
      setAuthDialogBusy(false);
      return;
    }
    setAuthDialogBusy(false);
    setAuthDialogOpen(false);
    setAuthSession(null);
    startProfilePolling();
  }, [authSession, browserConfig?.startUrl, queryClient, skill?.description, skill?.name, skill?.slug, startProfilePolling, stopBrowserSession, updateBrowserSkill]);

  const handleCancelAuthentication = useCallback(async () => {
    if (!authSession) {
      setAuthDialogOpen(false);
      setAuthStep("idle");
      return;
    }
    setAuthError(undefined);
    setAuthDialogBusy(true);
    try {
      await stopBrowserSession(authSession.sessionId, authSession.profileName);

      try {
        await deleteBrowserProfile(authSession.profileId);
      } catch (deleteError) {
        console.error("Failed to delete profile:", deleteError);
        setAuthError("Profile deletion failed. You may need to manually clean up.");
      }

      setAuthDialogOpen(false);
      setAuthSession(null);
      setAuthStep("idle");
    } catch {
      setAuthError("Failed to close authentication session.");
      setAuthDialogBusy(false);
      return;
    } finally {
      setAuthDialogBusy(false);
    }
  }, [authSession, deleteBrowserProfile, stopBrowserSession]);

  const handleAuthDialogChange = useCallback(
    (open: boolean) => {
      if (authDialogBusy) {
        return;
      }
      if (!open) {
        void handleCancelAuthentication();
        return;
      }
      setAuthDialogOpen(true);
    },
    [authDialogBusy, handleCancelAuthentication]
  );

  const handleManualCheck = useCallback(async () => {
    setAuthError(undefined);
    setAuthStep("verifying");
    const id = await fetchProfileId();
    if (id) {
      toast.success("Authentication connected.");
      setAuthStep("idle");
      return;
    }
    setAuthError("Authentication not found yet. Try again.");
    setAuthStep("idle");
  }, [fetchProfileId]);

  const handleOpenDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const { mutateAsync: deleteSkillMutation, isLoading: deletingSkill } = useMutation<
    Skill,
    unknown,
    string,
    { previousSkills?: Skill[] }
  >({
    mutationKey: ["deleteSkill"],
    mutationFn: deleteSkill,
    onMutate: async (skillIdValue) => {
      await queryClient.cancelQueries(["skills"]);
      const previousSkills = queryClient.getQueryData<Skill[]>(["skills"]);
      queryClient.setQueryData<Skill[]>(["skills"], (old = []) =>
        old.filter((item) => item.id !== skillIdValue)
      );
      return { previousSkills };
    },
    onError: (_error, _skillIdValue, context) => {
      if (context?.previousSkills) {
        queryClient.setQueryData(["skills"], context.previousSkills);
      }
      toast.error("Failed to delete skill");
    },
    onSuccess: () => {
      toast("Skill deleted successfully");
    },
    onSettled: (_data, _error, skillIdValue) => {
      queryClient.invalidateQueries(["skills"]);
      if (skillIdValue) {
        queryClient.invalidateQueries(["skill", skillIdValue]);
      }
    },
  });

  const handleSave = useCallback(async () => {
    if (!formState.isValid || !skillId) {
      formState.setShowErrors(true);
      return;
    }

    const values = formState.getValues();
    const shouldAttachProfile = Boolean(browserConfig?.profileName || profileId);
    await updateSkillMutation({
      id: skillId,
      name: values.name,
      description: values.description,
      slug: values.slug ?? buildSkillSlug(values.name),
      startUrl: values.startUrl,
      profileName: shouldAttachProfile ? profileName : undefined,
    });
  }, [browserConfig?.profileName, formState, profileId, profileName, skillId, updateSkillMutation]);

  const handleDeleteSkill = useCallback(async () => {
    if (!skill) {
      return;
    }
    setDeleteError(undefined);
    try {
      await deleteSkillMutation(skill.id);
      setDeleteDialogOpen(false);
      navigate("/skills");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Unable to delete skill. Remove linked simulator references and try again.";
      setDeleteError(message);
    }
  }, [deleteSkillMutation, navigate, skill]);

  const handleDeleteDialogChange = useCallback(
    (open: boolean) => {
      if (deletingSkill) {
        return;
      }
      setDeleteDialogOpen(open);
    },
    [deletingSkill]
  );

  const handleCloseDeleteDialog = useCallback(() => {
    handleDeleteDialogChange(false);
  }, [handleDeleteDialogChange]);

  if (isLoading) {
    return (
      <PageLayoutV2>
        <PageHeader title="Loading skill..." />
        <div className={styles.container}>
          <Text size="2" color="gray">
            Loading...
          </Text>
        </div>
      </PageLayoutV2>
    );
  }

  if (!skill) {
    return (
      <PageLayoutV2>
        <PageHeader title="Skill not found" />
        <div className={styles.container}>
          <Text size="2" color="gray">
            The requested skill could not be found.
          </Text>
          <Button color="gray" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </div>
      </PageLayoutV2>
    );
  }

  if (!isBrowserSkill(skill)) {
    return (
      <PageLayoutV2>
        <PageHeader title={skill.name} />
        <div className={styles.container}>
          <Text size="2" color="gray">
            This skill is not a browser skill.
          </Text>
        </div>
      </PageLayoutV2>
    );
  }

  return (
    <PageLayoutV2>
      <PageHeader
        title={
          <Flex align="center" gap="2" wrap="wrap">
            {formState.name || skill.name}
            <Badge color="orange" variant="soft">
              Browser
            </Badge>
          </Flex>
        }
        actions={
          <Flex gap="2" wrap="wrap">
            <Button
              variant="outline"
              color="gray"
              onClick={() => navigate('/skills')}
            >
              <ArrowLeftIcon />
              Back
            </Button>
            <Button
              variant="outline"
              color="gray"
              onClick={handleSave}
              disabled={isUpdating}
            >
              {isUpdating ? "Saving..." : "Save"}
            </Button>
            <DropdownMenu
              items={[
                {
                  label: "Delete skill",
                  icon: <TrashIcon />,
                  onClick: handleOpenDeleteDialog,
                },
              ]}
            >
              <DropdownMenu.Trigger>
                <Button
                  variant="outline"
                  color="gray"
                  disabled={deletingSkill || isUpdating}
                >
                  <DashboardSquareIcon size={12} fill="currentColor" />
                  Actions
                </Button>
              </DropdownMenu.Trigger>
            </DropdownMenu>
          </Flex>
        }
      />

      <div className={styles.container}>
        <div className={styles.panel}>
          <Flex direction="column" gap="3">
            <Text size="3" weight="medium">
              Details
            </Text>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <Text size="2" weight="medium">
                  Name
                </Text>
                <TextField.Root
                  size="3"
                  value={formState.name}
                  onChange={formState.handleNameChange}
                  placeholder="Browse Datadog"
                />
                {formState.showErrors && !formState.name.trim() && (
                  <Text size="1" className={styles.error}>
                    name is required
                  </Text>
                )}
              </div>

              <div className={styles.field}>
                <Text size="2" weight="medium">
                  Start URL
                </Text>
                <TextField.Root
                  size="3"
                  value={formState.startUrl}
                  onChange={formState.handleStartUrlChange}
                  placeholder="https://app.datadoghq.com"
                />
                <Text size="1" className={styles.helper}>
                  the browsing session will open at this URL
                </Text>
                {(formState.showErrors || formState.startUrl.length > 0) && !formState.urlValid && (
                  <Text size="1" className={styles.error}>
                    enter a valid URL with http or https
                  </Text>
                )}
              </div>
            </div>

            <div className={styles.field}>
              <Text size="2" weight="medium">
                Description
              </Text>
              <TextArea
                value={formState.description}
                onChange={formState.handleDescriptionChange}
                rows={4}
                placeholder="Describe what this browser skill should do."
              />
              {formState.showErrors && !formState.description.trim() && (
                <Text size="1" className={styles.error}>
                  description is required
                </Text>
              )}
            </div>

            {formState.isDuplicateSlug && (
              <Text size="1" className={styles.error}>
                slug {formState.slug} is already in use
              </Text>
            )}
          </Flex>
        </div>

        <div className={styles.panel}>
          <Flex direction="column" gap="3">
            <Flex justify="between" align="center" wrap="wrap" gap="3">
              <div className={styles.sectionHeading}>
                <Text size="3" weight="medium">
                  Authentication (Optional)
                </Text>
                <Text size="2" color="gray">
                  Some URLs require authentication. Launch remote browser to sign in directly in the in-app window.
                </Text>
              </div>
              <Flex gap="2" align="center">
                {authStep === "verifying" && (
                  <Badge color="blue" variant="soft">
                    Verifying…
                  </Badge>
                )}
                {authStep === "waiting" && (
                  <Badge color="blue" variant="soft">
                    Authentication in progress
                  </Badge>
                )}
                {profileId && (
                  <Badge color="green" variant="soft">
                    Created
                  </Badge>
                )}
              </Flex>
            </Flex>

            <Flex justify="start">
              <Button
                  highContrast
                onClick={handleStartAuthentication}
                disabled={
                  authDialogBusy ||
                  authStep === "starting" ||
                  authStep === "waiting" ||
                  deletingSkill ||
                  isUpdating
                }
              >
                {profileId ? "Reauthenticate" : "Start authentication"}
              </Button>
            </Flex>

            {authError && (
              <Text size="2" color="red">
                {authError}
              </Text>
            )}

            <Text size="1" color="gray">
              After you finish authentication in the in-app window, we will verify the profile
              <Badge color="gray" variant="soft">
                {profileName ?? ""}
              </Badge>
              . If it does not appear within a minute, try again.
            </Text>
          </Flex>
        </div>
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={handleDeleteDialogChange}>
        <Dialog.Content size="small" className={styles.confirmDialogContent}>
          <Flex direction="column" gap="3">
            <Text size="3" weight="medium">
              Delete skill
            </Text>
            <Text size="2" color="gray">
              Are you sure you want to delete "{skill.name}"?
            </Text>
            {deleteError && (
              <Text size="2" color="red">
                {deleteError}
              </Text>
            )}
            <Flex gap="2" justify="end">
              <Button
                variant="soft"
                color="gray"
                onClick={handleCloseDeleteDialog}
                disabled={deletingSkill}
              >
                Cancel
              </Button>
              <Button color="red" onClick={handleDeleteSkill} disabled={deletingSkill}>
                {deletingSkill ? "Deleting..." : "Delete skill"}
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog>

      <Dialog open={authDialogOpen} onOpenChange={handleAuthDialogChange}>
        {authSession ? (
          <Dialog.Content size="large" className={styles.authDialogContent}>
            <BrowserSkillAuthContent
              session={authSession}
              submitting={authDialogBusy}
              error={authError}
              onCancel={handleCancelAuthentication}
              onFinish={handleFinishAuthentication}
            />
          </Dialog.Content>
        ) : (
          <Dialog.Content size="medium" className={styles.authDialogContent}>
            <Flex direction="column" gap="2">
              <Text size="3" weight="medium">
                Authentication session closed
              </Text>
              <Text size="2" color="gray">
                Start authentication again to launch the in-app window.
              </Text>
            </Flex>
          </Dialog.Content>
        )}
      </Dialog>
    </PageLayoutV2>
  );
}
